import { useTranslation } from 'react-i18next';
import {
    PlanConfig as SharedPlanConfig,
    getAllActivePlans,
    mapLegacyTier,
} from '../../../shared/config/plans';

// Re-export types and utilities for frontend use
export type {
    PlanConfig,
    PlanFeature,
} from '../../../shared/config/plans';

// Frontend-specific plan interface that includes translated strings
export interface FrontendPlanConfig extends Omit<SharedPlanConfig, 'name' | 'description' | 'features'> {
    name: string; // Translated name
    description: string; // Translated description
    features: Array<{ text: string }>; // Translated features
    monthlyImageLimit: number;
}

export const usePlans = (): FrontendPlanConfig[] => {
    const { t } = useTranslation();

    return getAllActivePlans().map((plan: SharedPlanConfig) => ({
        ...plan,
        name: t(plan.translationKey),
        description: t(plan.descriptionTranslationKey),
        features: plan.features.map((feature) => ({
            text: t(feature.translationKey)
        })),
        // Keep legacy field name for compatibility
        dailyImageLimit: plan.monthlyImageLimit,
    }));
};

// Re-export shared utility functions
export {
    getPlanById as getSharedPlanById,
    getPlanByTier as getSharedPlanByTier,
    getTrialPlan,
    getPaidPlans as getSharedPaidPlans,
    getAllActivePlans,
    isValidPlanId,
    isTrialPlan,
    isPaidPlan,
    mapLegacyTier,
} from '../../../shared/config/plans';

// Legacy functions for backward compatibility with frontend components
export const getPlanByIdLegacy = (id: string, plans: FrontendPlanConfig[]): FrontendPlanConfig | undefined => {
    return plans.find(plan => plan.id === id);
};

export const getPlanByTierLegacy = (tier: string, plans: FrontendPlanConfig[]): FrontendPlanConfig | undefined => {
    // Handle legacy tier mapping
    const mappedTier = mapLegacyTier(tier);
    return plans.find(plan => plan.tier === mappedTier);
};

export const getFreePlan = (plans: FrontendPlanConfig[]): FrontendPlanConfig => {
    // Free plan is now trial plan
    const trialPlan = plans.find(plan => plan.tier === 'trial');
    if (!trialPlan) {
        throw new Error('Trial plan not found');
    }
    return trialPlan;
};

export const getPaidPlansLegacy = (plans: FrontendPlanConfig[]): FrontendPlanConfig[] => {
    return plans.filter(plan => plan.tier !== 'trial');
};
